import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { PinnableCard, ReportBackdrop, useReportCardMode } from '../components/crm/reportCards';
import { X, ChevronDown, Plus } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { getAccounts, addPurchase, getPurchases, updatePurchase, deletePurchase } from '../services/firestoreService';
import {
  GLASS_TEXT,
  GLASS_FIELD,
  GLASS_TH,
  GLASS_ROW,
  GLASS_INSET,
  GLASS_SECONDARY_BUTTON,
  GLASS_MODAL_OVERLAY,
  GLASS_MODAL_PANEL,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassBadge,
  GlassAccentButton,
  GlassAvatar,
  GlassEmpty,
  GlassSearch,
  SlidingPills,
} from '../components/glass/GlassUi';

const TAB_OPTIONS = ['Programs', 'A La Carte'].map(tab => ({ value: tab, label: tab }));
const DATE_OPTIONS = ['All Time', 'This Year', 'This Quarter', 'This Month'].map(period => ({ value: period, label: period }));

// One pinnable card per purchase type (My view shows each tab's table as its own card)
const TAB_CARD_IDS = { Programs: 'programs', 'A La Carte': 'a-la-carte' };

const ClientPurchases = () => {
  const cardMode = useReportCardMode();
  const [activeTab, setActiveTab] = useState('Programs');
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [dateFilter, setDateFilter] = useState('All Time');
  const [accounts, setAccounts] = useState([]);
  const [alaCarteItems, setAlaCarteItems] = useState([]);
  const [formData, setFormData] = useState({
    account: '',
    program: '',
    billingCycle: '',
    startDate: '',
    endDate: '',
    items: [],
    date: '',
    total: ''
  });
  const [selectedItems, setSelectedItems] = useState([]);

  // Define stages for each type
  const stages = {
    Programs: [
      { id: 'active', name: 'Active', count: 0 },
      { id: 'pending', name: 'Pending', count: 0 },
      { id: 'paused', name: 'Paused', count: 0 },
      { id: 'cancelled', name: 'Cancelled', count: 0 }
    ],
    'A La Carte': [
      { id: 'draft', name: 'Draft', count: 0 },
      { id: 'pending', name: 'Pending Payment', count: 0 },
      { id: 'paid', name: 'Paid', count: 0 },
      { id: 'completed', name: 'Completed', count: 0 }
    ]
  };

  const getTableHeaders = (tab = activeTab) => {
    if (tab === 'Programs') {
      return [
        { name: 'Account', sortable: true },
        { name: 'Program', sortable: true },
        { name: 'Total', sortable: true },
        { name: 'Billing Cycle', sortable: true },
        { name: 'Start Date', sortable: true },
        { name: 'End Date', sortable: true },
      ];
    } else {
      return [
        { name: 'Account', sortable: true },
        { name: 'Item', sortable: true },
        { name: 'Total', sortable: true },
        { name: 'Date', sortable: true },
      ];
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch purchases using the service function
        const purchasesData = await getPurchases();
        setPurchases(purchasesData);

        // Fetch accounts
        const accountsData = await getAccounts();
        setAccounts(accountsData);

        // Fetch A La Carte items
        const alaCarteSnapshot = await getDocs(collection(db, 'alaCarteItems'));
        const alaCarteData = alaCarteSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAlaCarteItems(alaCarteData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft':
        return 'text-gray-600';
      case 'Pending':
        return 'text-yellow-600';
      case 'Paid':
      case 'Active':
        return 'text-green-600';
      case 'Paused':
        return 'text-blue-600';
      case 'Cancelled':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Active':
        return { text: 'text-green-600', border: 'border-green-500' };
      case 'Pending':
        return { text: 'text-yellow-600', border: 'border-yellow-500' };
      case 'Paused':
        return { text: 'text-blue-600', border: 'border-blue-500' };
      case 'Cancelled':
        return { text: 'text-red-600', border: 'border-red-500' };
      case 'Draft':
        return { text: 'text-gray-600', border: 'border-gray-500' };
      case 'Paid':
        return { text: 'text-green-600', border: 'border-green-500' };
      case 'Completed':
        return { text: 'text-green-600', border: 'border-green-500' };
      default:
        return { text: 'text-gray-600', border: 'border-gray-500' };
    }
  };

  const purchasesForTab = (tab) => purchases.filter(purchase => {
    const matchesSearch = !searchQuery || 
      purchase.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      purchase.contactName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = purchase.type === tab;
    return matchesSearch && matchesType;
  });

  const filteredPurchases = purchasesForTab(activeTab);

  const purchasesByStage = stages[activeTab].reduce((acc, stage) => {
    acc[stage.name] = filteredPurchases.filter(purchase => purchase.status === stage.name);
    return acc;
  }, {});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleItemSelect = (itemId) => {
    const item = alaCarteItems.find(i => i.id === itemId);
    if (item && !selectedItems.find(selected => selected.id === itemId)) {
      const newSelectedItems = [...selectedItems, item];
      setSelectedItems(newSelectedItems);
      setFormData(prev => ({
        ...prev,
        items: newSelectedItems.map(item => item.id)
      }));
    }
  };

  const handleItemRemove = (itemId) => {
    const newSelectedItems = selectedItems.filter(item => item.id !== itemId);
    setSelectedItems(newSelectedItems);
    setFormData(prev => ({
      ...prev,
      items: newSelectedItems.map(item => item.id)
    }));
  };

  const handleRowClick = (purchase) => {
    setEditingPurchase(purchase);
    setActiveTab(purchase.type);
    
    // Pre-populate form with existing data
    const selectedAccount = accounts.find(account => account.id === purchase.accountId);
    setFormData({
      account: purchase.accountId || '',
      program: purchase.program || '',
      billingCycle: purchase.billingCycle || '',
      startDate: purchase.startDate ? (purchase.startDate instanceof Date ? purchase.startDate.toISOString().split('T')[0] : new Date(purchase.startDate.seconds * 1000).toISOString().split('T')[0]) : '',
      endDate: purchase.endDate ? (purchase.endDate instanceof Date ? purchase.endDate.toISOString().split('T')[0] : new Date(purchase.endDate.seconds * 1000).toISOString().split('T')[0]) : '',
      items: purchase.items || [],
      date: purchase.date ? (purchase.date instanceof Date ? purchase.date.toISOString().split('T')[0] : new Date(purchase.date.seconds * 1000).toISOString().split('T')[0]) : '',
      total: purchase.total?.toString() || ''
    });

    // Pre-populate selected items for A La Carte
    if (purchase.type === 'A La Carte' && purchase.items) {
      const itemsToSelect = alaCarteItems.filter(item => purchase.items.includes(item.id));
      setSelectedItems(itemsToSelect);
    } else {
      setSelectedItems([]);
    }

    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingPurchase(null);
    setFormData({
      account: '',
      program: '',
      billingCycle: '',
      startDate: '',
      endDate: '',
      items: [],
      date: '',
      total: ''
    });
    setSelectedItems([]);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!editingPurchase) return;

    try {
      setLoading(true);
      
      // Delete from Firestore
      await deletePurchase(editingPurchase.id);
      
      // Remove from local state
      setPurchases(prev => prev.filter(p => p.id !== editingPurchase.id));
      
      // Close modals and reset
      setShowDeleteConfirm(false);
      handleCloseEditModal();
      
      console.log('Purchase deleted successfully');
    } catch (error) {
      console.error('Error deleting purchase:', error);
      alert('Failed to delete purchase. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate A La Carte items
    if (activeTab === 'A La Carte' && selectedItems.length === 0) {
      alert('Please select at least one item for A La Carte purchase.');
      return;
    }
    
    try {
      setLoading(true);
      
      // Find the selected account details
      const selectedAccount = accounts.find(account => account.id === formData.account);
      if (!selectedAccount) {
        alert('Please select a valid account.');
        return;
      }

      // Prepare purchase data
      const purchaseData = {
        type: activeTab,
        accountId: formData.account,
        company: selectedAccount.company,
        contactName: selectedAccount.contact?.firstName && selectedAccount.contact?.lastName 
          ? `${selectedAccount.contact.firstName} ${selectedAccount.contact.lastName}`
          : selectedAccount.contact?.email || '',
        total: parseFloat(formData.total) || 0,
        status: editingPurchase ? editingPurchase.status : (activeTab === 'Programs' ? 'Pending' : 'Draft')
      };

      // Add type-specific fields
      if (activeTab === 'Programs') {
        purchaseData.program = formData.program;
        purchaseData.billingCycle = formData.billingCycle;
        purchaseData.startDate = formData.startDate;
        purchaseData.endDate = formData.endDate;
      } else if (activeTab === 'A La Carte') {
        purchaseData.items = selectedItems.map(item => item.id);
        purchaseData.item = selectedItems.map(item => item.name).join(', ');
        purchaseData.date = formData.date;
      }

      let updatedPurchase;
      if (editingPurchase) {
        // Update existing purchase
        updatedPurchase = await updatePurchase(editingPurchase.id, purchaseData);
        
        // Update local state
        setPurchases(prev => prev.map(p => p.id === editingPurchase.id ? updatedPurchase : p));
        
        // Close edit modal and reset form
        handleCloseEditModal();
        
        console.log('Purchase updated successfully:', updatedPurchase);
      } else {
        // Add new purchase
        updatedPurchase = await addPurchase(purchaseData);
        
        // Add to local state
        setPurchases(prev => [updatedPurchase, ...prev]);
        
        // Close add modal and reset form
        setShowAddModal(false);
        setFormData({
          account: '',
          program: '',
          billingCycle: '',
          startDate: '',
          endDate: '',
          items: [],
          date: '',
          total: ''
        });
        setSelectedItems([]);
        
        console.log('Purchase added successfully:', updatedPurchase);
      }
    } catch (error) {
      console.error('Error saving purchase:', error);
      alert(`Failed to ${editingPurchase ? 'update' : 'add'} purchase. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value) => (value
    ? (value instanceof Date ? value.toLocaleDateString() : new Date(value.seconds * 1000).toLocaleDateString())
    : 'N/A');

  const labelCls = 'mb-1 block text-sm font-medium text-gray-700 dark:text-white/70';
  const selectCls = `${GLASS_FIELD} appearance-none pr-8 dark:[&>option]:bg-[#14110f]`;
  const chevron = <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-white/50" />;
  const closeBtnCls = 'grid h-8 w-8 place-items-center rounded-full text-gray-500 transition hover:bg-black/[0.05] hover:text-[#F80707] dark:text-white/50 dark:hover:bg-white/[0.06] dark:hover:text-[#FF4D40]';

  // Shared by the Add and Edit modals (a render function, not a component, so inputs keep focus)
  const renderPurchaseFields = () => (
    <>
      {/* Account */}
      <div>
        <label className={labelCls}>
          Account
        </label>
        <div className="relative">
          <select
            name="account"
            value={formData.account}
            onChange={handleInputChange}
            className={selectCls}
            required
          >
            <option value="">Select an account</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.company}
              </option>
            ))}
          </select>
          {chevron}
        </div>
      </div>

      {activeTab === 'Programs' ? (
        <>
          {/* Program */}
          <div>
            <label className={labelCls}>
              Program
            </label>
            <div className="relative">
              <select
                name="program"
                value={formData.program}
                onChange={handleInputChange}
                className={selectCls}
                required
              >
                <option value="">Select a program</option>
                <option value="Lite">Lite</option>
                <option value="Plus">Plus</option>
                <option value="Pro">Pro</option>
                <option value="Other">Other</option>
              </select>
              {chevron}
            </div>
          </div>

          {/* Billing Cycle */}
          <div>
            <label className={labelCls}>
              Billing Cycle
            </label>
            <div className="relative">
              <select
                name="billingCycle"
                value={formData.billingCycle}
                onChange={handleInputChange}
                className={selectCls}
                required
              >
                <option value="">Select billing cycle</option>
                <option value="Monthly">Monthly</option>
                <option value="Annual">Annual</option>
              </select>
              {chevron}
            </div>
          </div>

          {/* Start Date */}
          <div>
            <label className={labelCls}>
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              className={`${GLASS_FIELD} dark:[color-scheme:dark]`}
              required
            />
          </div>

          {/* End Date */}
          <div>
            <label className={labelCls}>
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              className={`${GLASS_FIELD} dark:[color-scheme:dark]`}
              required
            />
          </div>
        </>
      ) : (
        <>
          {/* Items */}
          <div>
            <label className={labelCls}>
              Items
            </label>

            {/* Selected Items Display */}
            {selectedItems.length > 0 && (
              <div className="mb-2">
                <div className="mb-1 text-xs text-gray-500 dark:text-white/[0.45]">
                  Selected Items ({selectedItems.length}):
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-1 rounded-full border border-black/[0.05] bg-black/[0.04] py-1 pl-3 pr-1 text-sm text-gray-800 dark:border-white/[0.06] dark:bg-white/[0.06] dark:text-white/[0.85]"
                    >
                      <span>{item.icon}</span>
                      <span>{item.name}</span>
                      <button
                        type="button"
                        onClick={() => handleItemRemove(item.id)}
                        className="ml-1 rounded-full p-1 text-gray-500 transition hover:bg-[#F80707] hover:text-white dark:text-white/50"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Item Selection Dropdown */}
            <div className="relative">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleItemSelect(e.target.value);
                    e.target.value = ''; // Reset selection
                  }
                }}
                className={selectCls}
              >
                <option value="">Add an item</option>
                {alaCarteItems
                  .filter(item => !selectedItems.find(selected => selected.id === item.id))
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.icon} {item.name}
                    </option>
                  ))}
              </select>
              {chevron}
            </div>

            {selectedItems.length === 0 && (
              <div className="mt-1 text-xs text-gray-500 dark:text-white/[0.45]">
                Select at least one item
              </div>
            )}
          </div>

          {/* Date */}
          <div>
            <label className={labelCls}>
              Date
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              className={`${GLASS_FIELD} dark:[color-scheme:dark]`}
              required
            />
          </div>
        </>
      )}

      {/* Total */}
      <div>
        <label className={labelCls}>
          {activeTab === 'Programs' ? 'Monthly Total' : 'Total'}
        </label>
        <input
          type="number"
          step="0.01"
          name="total"
          value={formData.total}
          onChange={handleInputChange}
          className={GLASS_FIELD}
          placeholder="0.00"
          required
        />
      </div>
    </>
  );

  // One purchase-type table card (a render function, not a component, so it isn't remounted on every render)
  const renderPurchasesCard = (tab) => {
    const tabPurchases = purchasesForTab(tab);
    return (
      <PinnableCard id={TAB_CARD_IDS[tab]} title={`${tab} Purchases`} size="wide">
      <GlassCard className="p-5" delay={0}>
        <GlassCardHeader
          title={tab}
          right={!loading && !error && (
            <span className="text-xs text-gray-500 dark:text-white/50">{tabPurchases.length} purchases</span>
          )}
        />
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <BBALoader size={96} />
          </div>
        ) : error ? (
          <GlassEmpty className={`py-8 ${GLASS_TEXT.red}`}>{error}</GlassEmpty>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  {getTableHeaders(tab).map((header) => (
                    <th key={header.name} className={GLASS_TH}>
                      <div className="flex items-center">
                        <span>{header.name}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-gray-700 dark:text-white/75">
                {tabPurchases.length > 0 ? (
                  tabPurchases.map((purchase) => (
                    <tr
                      key={purchase.id}
                      onClick={() => handleRowClick(purchase)}
                      className={`${GLASS_ROW} cursor-pointer`}
                    >
                      {/* Account column - same for both tabs */}
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-3">
                          <GlassAvatar name={purchase.company || ''} />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{purchase.company || 'Unnamed Company'}</div>
                          </div>
                        </div>
                      </td>

                      {tab === 'Programs' ? (
                        <>
                          {/* Program */}
                          <td className="py-3 pr-4">
                            {purchase.program ? <GlassBadge tone="blue">{purchase.program}</GlassBadge> : 'N/A'}
                          </td>
                          {/* Total */}
                          <td className="py-3 pr-4 font-semibold tabular-nums text-gray-900 dark:text-white">${purchase.total?.toLocaleString() || '0'}</td>
                          {/* Billing Cycle */}
                          <td className="py-3 pr-4">{purchase.billingCycle || 'N/A'}</td>
                          {/* Start Date */}
                          <td className="py-3 pr-4 whitespace-nowrap">
                            {formatDate(purchase.startDate)}
                          </td>
                          {/* End Date */}
                          <td className="py-3 whitespace-nowrap">
                            {formatDate(purchase.endDate)}
                          </td>
                        </>
                      ) : (
                        <>
                          {/* Item */}
                          <td className="py-3 pr-4">{purchase.item || 'N/A'}</td>
                          {/* Total */}
                          <td className="py-3 pr-4 font-semibold tabular-nums text-gray-900 dark:text-white">${purchase.total?.toLocaleString() || '0'}</td>
                          {/* Date */}
                          <td className="py-3 whitespace-nowrap">
                            {formatDate(purchase.date)}
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tab === 'Programs' ? 6 : 4} className="py-8 text-center text-gray-500 dark:text-white/40">
                      No purchases found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
      </PinnableCard>
    );
  };

  return (
    <GlassPage className="text-gray-900 dark:text-white" backdrop={<ReportBackdrop />}>
      <GlassPageHeader
        backTo="/crm/reports"
        backTitle="Back to reports"
        center={<SlidingPills options={TAB_OPTIONS} value={activeTab} onChange={setActiveTab} ariaLabel="Purchase type" />}
        title={<>Client<br />Purchases</>}
        controls={(
          <>
            {/* Search and Filters */}
            <GlassSearch
              className="w-full sm:w-72"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            {/* Date Range Filter */}
            <SlidingPills size="sm" options={DATE_OPTIONS} value={dateFilter} onChange={setDateFilter} ariaLabel="Date range" />

            {/* New Button */}
            <GlassAccentButton onClick={() => setShowAddModal(true)}>
              <Plus className="h-4 w-4" />
              <span>New</span>
            </GlassAccentButton>
          </>
        )}
      />

      {/* Content */}
      {cardMode
        ? TAB_OPTIONS.map(({ value }) => <React.Fragment key={value}>{renderPurchasesCard(value)}</React.Fragment>)
        : renderPurchasesCard(activeTab)}

      {/* Modals are kept outside the glass card: backdrop-filter would trap a fixed overlay */}
      {/* Add New Modal */}
      {showAddModal && (
        <div className={GLASS_MODAL_OVERLAY}>
          <div className={GLASS_MODAL_PANEL}>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex-1"></div>
              <h2 className="flex-1 text-center text-xl font-medium">
                Add Purchase
              </h2>
              <div className="flex flex-1 justify-end">
                <button
                  onClick={() => setShowAddModal(false)}
                  className={closeBtnCls}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {renderPurchaseFields()}

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className={`${GLASS_SECONDARY_BUTTON} flex-1 justify-center`}
                >
                  Cancel
                </button>
                <GlassAccentButton
                  type="submit"
                  disabled={loading}
                  className="flex-1 justify-center"
                >
                  {loading ? 'Adding...' : 'Add Purchase'}
                </GlassAccentButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Purchase Modal */}
      {showEditModal && (
        <div className={GLASS_MODAL_OVERLAY}>
          <div className={GLASS_MODAL_PANEL}>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex-1"></div>
              <h2 className="flex-1 text-center text-xl font-medium">
                Edit Purchase
              </h2>
              <div className="flex flex-1 justify-end">
                <button
                  onClick={handleCloseEditModal}
                  className={closeBtnCls}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {renderPurchaseFields()}

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  disabled={loading}
                  className="inline-flex items-center justify-center rounded-full border border-[#F80707]/40 px-4 py-2 text-sm font-medium text-[#E00606] transition hover:bg-[#F80707]/[0.08] disabled:cursor-not-allowed disabled:opacity-40 dark:border-[#F80707]/50 dark:text-[#FF4D40] dark:hover:bg-[#F80707]/[0.12]"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className={`${GLASS_SECONDARY_BUTTON} flex-1 justify-center`}
                >
                  Cancel
                </button>
                <GlassAccentButton
                  type="submit"
                  disabled={loading}
                  className="flex-1 justify-center"
                >
                  {loading ? 'Saving...' : 'Save'}
                </GlassAccentButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className={GLASS_MODAL_OVERLAY}>
          <div className={GLASS_MODAL_PANEL}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-medium">
                Delete Purchase
              </h2>
              <button
                onClick={handleDeleteCancel}
                className={closeBtnCls}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="mb-2 text-gray-600 dark:text-white/70">
                Are you sure you want to delete this purchase?
              </p>
              {editingPurchase && (
                <div className={`${GLASS_INSET} space-y-0.5 p-3 text-sm text-gray-700 dark:text-white/75`}>
                  <p>
                    <strong>Company:</strong> {editingPurchase.company}
                  </p>
                  <p>
                    <strong>Type:</strong> {editingPurchase.type}
                  </p>
                  {editingPurchase.type === 'Programs' && (
                    <p>
                      <strong>Program:</strong> {editingPurchase.program}
                    </p>
                  )}
                  <p>
                    <strong>Total:</strong> ${editingPurchase.total?.toLocaleString() || '0'}
                  </p>
                </div>
              )}
              <p className={`mt-2 text-sm ${GLASS_TEXT.red}`}>
                This action cannot be undone.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDeleteCancel}
                disabled={loading}
                className={`${GLASS_SECONDARY_BUTTON} flex-1 justify-center`}
              >
                Cancel
              </button>
              <GlassAccentButton
                onClick={handleDeleteConfirm}
                disabled={loading}
                className="flex-1 justify-center"
              >
                {loading ? 'Deleting...' : 'Delete Purchase'}
              </GlassAccentButton>
            </div>
          </div>
        </div>
      )}
    </GlassPage>
  );
};

export default ClientPurchases;
